import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext'; 

// We will add these functions to db.js next
import { getDailyTasks, addDailyTask, toggleTaskCompletion, getSystemPulse, getOperatorPulse } from '../../services/db';

export default function DailyStrikeZone({ isOperatorView = false }) {
  const { currentUser } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant(); 
  
  const [isLoading, setIsLoading] = useState(true);

  // Live Tactical Clock
  const [time, setTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formattedDate = time.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  const formattedTime = time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  // ==========================================
  // UNIFIED KINETIC STATE
  // ==========================================
  const [tasks, setTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  // Founder Metrics State
  const [systemPulse, setSystemPulse] = useState({
    activeTenants: 0, totalFleet: 0, pendingApprovals: 0
  });

  // Operator Metrics State
  const [operatorMetrics, setOperatorMetrics] = useState({
    name: "", tier: "Standard 1099", mtdCommission: 0, pendingPayout: 0, activeClients: 0, dealsInPipeline: 0
  });

  // --- ⚡️ LIVE FIREBASE FETCH (MULTI-TENANT) ⚡️ ---
  useEffect(() => {
    const fetchStrikeZoneData = async () => {
      if (tenantLoading || !tenantId) return;

      setIsLoading(true);
      try {
        const uid = currentUser?.uid;
        if (!uid) return;

        // 1. Fetch Kinetic Task List (Scoped to active tenant)
        const rawTasks = await getDailyTasks(tenantId, uid, isOperatorView);
        
        // INTERCEPT & MAP: Translate incoming webhook calendar blocks into UI-ready tasks
        const formattedTasks = rawTasks.map(task => {
          if (task.type === 'meeting') {
            const personalName = task.firstName ? `${task.firstName} ${task.lastName || ''}`.trim() : null;
            const entityFallback = task.company || task.entityName || personalName || 'Unknown Client';
            
            return {
              ...task,
              id: task.id,
              category: 'Meeting',
              title: task.title || 'Scheduled Intake Appointment',
              target: entityFallback,
              time: task.appointmentTime || task.time || 'TBD',
              priority: 'High', // Force high priority for scheduled calendar blocks
              completed: !!task.completed
            };
          }
          return task;
        });

        setTasks(formattedTasks);

        // 2. Fetch Context-Specific Pulse
        if (isOperatorView) {
          const metrics = await getOperatorPulse(tenantId, uid);
          setOperatorMetrics({
            name: currentUser?.email?.split('@')[0] || "Operator",
            ...metrics
          });
        } else {
          const pulse = await getSystemPulse(tenantId);
          setSystemPulse(pulse);
        }
      } catch (error) {
        console.error("Failed to sync Strike Zone:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStrikeZoneData();
  }, [currentUser, isOperatorView, tenantId, tenantLoading]);

  // ==========================================
  // TASK ENGINE LOGIC
  // ==========================================
  const toggleTask = async (id, currentStatus) => {
    if (!tenantId) return;

    // 1. Optimistic UI Update
    setTasks(tasks.map(task => 
      task.id === id ? { ...task, completed: !currentStatus } : task
    ));

    // 2. Background Database Write
    try {
      await toggleTaskCompletion(tenantId, currentUser.uid, id, !currentStatus);
    } catch (error) {
      console.error("Failed to toggle task in DB", error);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim() || !tenantId) return;
    
    const taskPayload = {
      category: isOperatorView ? 'Follow Up' : 'Operations', 
      title: newTaskTitle.trim(),
      time: formattedTime,
      completed: false,
      priority: 'Medium'
    };
    
    // 1. Optimistic Add (Generate a temporary ID so UI updates instantly)
    const tempId = `temp_${Date.now()}`;
    setTasks(prev => [...prev, { id: tempId, ...taskPayload }]);
    setNewTaskTitle('');

    // 2. Background Database Write
    try {
      const realId = await addDailyTask(tenantId, currentUser.uid, isOperatorView, taskPayload);
      
      // Swap the temp ID for the real Firebase Document ID
      setTasks(prev => prev.map(t => t.id === tempId ? { ...t, id: realId } : t));
    } catch (error) {
      console.error("Failed to add task to DB", error);
    }
  };

  const completedCount = tasks.filter(t => t.completed).length;
  const progressPercentage = tasks.length === 0 ? 0 : Math.round((completedCount / tasks.length) * 100);

  // Dynamic badge styling helper for both views
  const getCategoryBadgeClass = (category) => {
    switch(category) {
      case 'Meeting': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'Merchandising': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Operations': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Pipeline': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  // ==========================================
  // VIEW RENDERER: OPERATOR
  // ==========================================
  if (isOperatorView) {
    return (
      <div className="animate-in fade-in duration-500 pb-12 relative">
        <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4">
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Operator: {operatorMetrics.name} • {operatorMetrics.tier}</p>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Tactical Strike Zone</h1>
          </div>
          <div className="flex gap-4">
            <div className="text-right border-r border-slate-300 pr-4">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">MTD Commission</p>
              <p className="text-xl font-black text-emerald-600">${(operatorMetrics.mtdCommission || 0).toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Pending Payout</p>
              <p className="text-xl font-black text-slate-800">${(operatorMetrics.pendingPayout || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column: Action Queue */}
          <div className="xl:col-span-2 space-y-6">
            <div className="bg-white border border-slate-300 shadow-sm flex flex-col h-full">
              <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex justify-between items-center">
                <h2 className="text-[10px] font-bold text-white uppercase tracking-widest flex items-center gap-2">
                  <i className="fa-solid fa-crosshairs text-emerald-500"></i> The Hit List (Action Queue)
                </h2>
                {progressPercentage === 100 && tasks.length > 0 && (
                  <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest px-2 py-0.5 bg-emerald-100/10 border border-emerald-500/30">
                    Zone Cleared
                  </span>
                )}
              </div>
              
              <div className="divide-y divide-slate-100 flex-1">
                {isLoading || tenantLoading ? (
                   <div className="p-12 text-center">
                     <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                     <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Syncing Objectives...</p>
                   </div>
                ) : tasks.length === 0 ? (
                  <div className="p-12 text-center">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Queue is empty</p>
                    <p className="text-xs text-slate-400 font-medium">Add a new directive below to build your pipeline.</p>
                  </div>
                ) : (
                  tasks.map((task) => {
                    const isUrgent = task.priority === 'High';
                    
                    return (
                      <div key={task.id} className={`p-4 flex items-start gap-4 transition-colors ${isUrgent && !task.completed ? 'bg-red-50/30' : 'hover:bg-slate-50'}`}>
                        <div className="mt-1 flex-shrink-0 cursor-pointer" onClick={() => toggleTask(task.id, task.completed)}>
                          {task.completed ? (
                            <i className="fa-solid fa-circle-check text-emerald-500 text-xl"></i>
                          ) : isUrgent ? (
                            <i className="fa-solid fa-circle-exclamation text-red-500 text-xl animate-pulse"></i>
                          ) : (
                            <i className="fa-regular fa-circle text-slate-300 text-xl hover:text-emerald-400 transition-colors"></i>
                          )}
                        </div>

                        <div className={`flex-1 transition-opacity ${task.completed ? 'opacity-50' : 'opacity-100'}`}>
                          <div className="flex justify-between items-start mb-1">
                            <div>
                              <span className={`text-[9px] font-bold uppercase tracking-widest mr-2 px-2 py-0.5 border ${getCategoryBadgeClass(task.category)}`}>
                                {task.category}
                              </span>
                              {task.target && <span className="text-sm font-bold text-slate-900">{task.target}</span>}
                            </div>
                            <span className={`text-[10px] font-bold uppercase tracking-widest ${isUrgent && !task.completed ? 'text-red-600' : 'text-slate-500'}`}>
                              {task.time}
                            </span>
                          </div>
                          <p className={`text-sm font-medium ${task.completed ? 'text-slate-400 line-through' : 'text-slate-600'}`}>{task.title}</p>
                          
                          {!task.completed && (
                            <div className="mt-3 flex gap-2">
                              <button onClick={() => toggleTask(task.id, task.completed)} className="text-[9px] font-bold text-slate-500 border border-slate-300 bg-white hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300 px-3 py-1.5 uppercase tracking-widest transition-colors">
                                <i className="fa-solid fa-check mr-1"></i> Mark Complete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>

              {/* Unified Quick Add Form */}
              <form onSubmit={handleAddTask} className="border-t border-slate-200 bg-slate-50 p-4 flex gap-3">
                <input 
                  type="text" 
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="ADD NEW DIRECTIVE..." 
                  className="flex-1 bg-white border border-slate-300 text-[10px] font-bold uppercase tracking-widest px-4 py-2.5 focus:outline-none focus:border-emerald-500 text-slate-800 placeholder:text-slate-400"
                />
                <button 
                  type="submit" 
                  disabled={!newTaskTitle.trim() || tenantLoading}
                  className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:text-slate-500 text-white text-[10px] font-bold px-6 uppercase tracking-widest transition-colors shadow-sm"
                >
                  Append
                </button>
              </form>
            </div>
          </div>

          {/* Right Column: Quick Actions & Funnel Health */}
          <div className="space-y-6">
            <div className="bg-white border border-slate-300 shadow-sm p-4">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4">Command Deck</h3>
              <div className="space-y-3">
                <Link to="/operator/intake" className="w-full flex items-center justify-between bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-4 transition-colors shadow-sm cursor-pointer group">
                  <span className="text-[10px] uppercase tracking-widest">Submit New Deal</span>
                  <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                </Link>
                <button className="w-full flex items-center justify-between bg-white border border-slate-300 hover:border-slate-500 text-slate-700 font-bold py-3 px-4 transition-colors">
                  <span className="text-[10px] uppercase tracking-widest">Modify / Upsell Client</span>
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>
                <Link to="/operator/resources" className="w-full flex items-center justify-between bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 px-4 transition-colors">
                  <span className="text-[10px] uppercase tracking-widest">View Pitch Deck</span>
                  <i className="fa-solid fa-file-pdf"></i>
                </Link>
              </div>
            </div>

            <div className="bg-white border border-slate-300 shadow-sm p-4">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4">Funnel Health</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                    <span>Active Clients Managed</span>
                    <span className="text-slate-900">{isLoading || tenantLoading ? '-' : operatorMetrics.activeClients}</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100">
                    <div className="h-full bg-blue-500 transition-all duration-1000" style={{width: `${Math.min((operatorMetrics.activeClients / 20) * 100, 100)}%`}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                    <span>Deals in Pipeline</span>
                    <span className="text-slate-900">{isLoading || tenantLoading ? '-' : operatorMetrics.dealsInPipeline}</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100">
                    <div className="h-full bg-amber-500 transition-all duration-1000" style={{width: `${Math.min((operatorMetrics.dealsInPipeline / 10) * 100, 100)}%`}}></div>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100">
                <Link to="/operator/pipeline" className="text-[9px] font-bold text-emerald-600 hover:text-emerald-700 uppercase tracking-widest w-full text-center block transition-colors">
                  Open Full Pipeline View <i className="fa-solid fa-arrow-up-right-from-square ml-1"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW RENDERER: FOUNDER
  // ==========================================
  return (
    <div className="animate-in fade-in duration-500 pb-12 h-full">
      <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Daily Strike Zone</h1>
          <p className="text-slate-500 text-sm mt-1 font-medium">Your kinetic operational checklist. Execute and advance.</p>
        </div>
        <div className="text-right flex items-center gap-6">
          <div className="border-r border-slate-300 pr-6 hidden md:block">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">{formattedDate}</p>
            <p className="text-xl font-black text-slate-900 tracking-widest">{formattedTime}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Execution Rate</p>
            <p className="text-2xl font-black text-emerald-700">{progressPercentage}%</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: The Interactive Checklist */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-300 shadow-sm p-4">
            <div className="w-full bg-slate-100 h-2 rounded-none overflow-hidden flex">
              <div 
                className="bg-emerald-500 h-full transition-all duration-700 ease-out relative" 
                style={{ width: `${progressPercentage}%` }}
              >
                <div className="absolute inset-0 bg-white/20 w-full h-full animate-[shimmer_2s_infinite]"></div>
              </div>
            </div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-3 text-right">
              {completedCount} of {tasks.length} Objectives Cleared
            </p>
          </div>

          <div className="bg-white border border-slate-300 shadow-sm flex flex-col">
            <div className="bg-slate-100 border-b border-slate-300 px-4 py-3 flex justify-between items-center">
              <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Active Directives</h3>
              {progressPercentage === 100 && tasks.length > 0 && (
                <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest px-2 py-0.5 bg-emerald-100">
                  Zone Cleared
                </span>
              )}
            </div>
            
            <div className="divide-y divide-slate-200 flex-1">
              {isLoading || tenantLoading ? (
                <div className="p-12 text-center">
                  <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Syncing Objectives...</p>
                </div>
              ) : tasks.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <p className="text-xs font-bold uppercase tracking-widest">No active directives.</p>
                </div>
              ) : (
                tasks.map(task => (
                  <div 
                    key={task.id} 
                    onClick={() => toggleTask(task.id, task.completed)}
                    className={`px-4 py-4 flex items-start gap-4 transition-colors cursor-pointer group ${task.completed ? 'bg-slate-50/50' : 'hover:bg-slate-50'}`}
                  >
                    <div className={`mt-0.5 flex-shrink-0 w-5 h-5 border flex items-center justify-center transition-colors ${task.completed ? 'bg-emerald-500 border-emerald-500' : 'border-slate-400 bg-white group-hover:border-emerald-500'}`}>
                      {task.completed && <i className="fa-solid fa-check text-white text-[10px]"></i>}
                    </div>
                    
                    <div className={`flex-1 transition-opacity ${task.completed ? 'opacity-50' : 'opacity-100'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-widest border ${getCategoryBadgeClass(task.category)}`}>
                          {task.category}
                        </span>
                        {task.priority === 'High' && !task.completed && (
                          <span className="text-[9px] font-bold text-red-600 uppercase tracking-widest flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span> Priority
                          </span>
                        )}
                      </div>
                      <p className={`text-sm font-bold transition-all ${task.completed ? 'text-slate-400 line-through' : 'text-slate-900 group-hover:text-emerald-700'}`}>
                        {task.title}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${task.completed ? 'text-slate-400' : 'text-slate-500'}`}>
                        {task.time}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleAddTask} className="border-t border-slate-200 bg-slate-50 p-4 flex gap-3">
              <input 
                type="text" 
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                placeholder="ADD NEW DIRECTIVE..." 
                className="flex-1 bg-white border border-slate-300 text-[10px] font-bold uppercase tracking-widest px-4 py-2.5 focus:outline-none focus:border-emerald-500 text-slate-800 placeholder:text-slate-400"
              />
              <button 
                type="submit" 
                disabled={!newTaskTitle.trim() || tenantLoading}
                className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:text-slate-500 text-white text-[10px] font-bold px-6 uppercase tracking-widest transition-colors shadow-sm"
              >
                Append
              </button>
            </form>
          </div>
        </div>

        {/* Right Column: Quick Stats & Actions */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-300 shadow-sm">
            <div className="bg-slate-100 border-b border-slate-300 px-4 py-3">
              <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Command Overrides</h3>
            </div>
            <div className="p-4 space-y-3">
              <Link to="/genesis" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-widest shadow-sm transition-colors">
                <i className="fa-solid fa-server"></i> Open Genesis Gateway
              </Link>
              <Link to="/pipeline" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold uppercase tracking-widest border border-slate-900 transition-colors">
                <i className="fa-solid fa-bars-staggered"></i> View Pipeline Board
              </Link>
              <Link to="/telemetry" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-[10px] font-bold uppercase tracking-widest transition-colors">
                <i className="fa-solid fa-satellite-dish"></i> Launch Telemetry
              </Link>
            </div>
          </div>

          <div className="bg-white border border-slate-300 shadow-sm">
            <div className="bg-slate-100 border-b border-slate-300 px-4 py-3 flex justify-between items-center">
              <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">System Pulse</h3>
              <span className="flex items-center gap-1 text-[9px] font-bold text-emerald-600 uppercase tracking-widest">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span> Online
              </span>
            </div>
            <div className="p-4 divide-y divide-slate-100">
              <div className="py-2 flex justify-between items-center">
                <span className="text-xs font-bold text-slate-500">Active Tenants</span>
                <span className="text-sm font-black text-slate-800">{isLoading || tenantLoading ? '-' : systemPulse.activeTenants}</span>
              </div>
              <div className="py-2 flex justify-between items-center">
                <span className="text-xs font-bold text-slate-500">Total 1099 Fleet</span>
                <span className="text-sm font-black text-slate-800">{isLoading || tenantLoading ? '-' : systemPulse.totalFleet}</span>
              </div>
              <div className="py-2 flex justify-between items-center">
                <span className="text-xs font-bold text-slate-500">Pending Approvals</span>
                <span className="text-sm font-black text-amber-600">{isLoading || tenantLoading ? '-' : systemPulse.pendingApprovals}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}